import React from 'react';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { Card } from '../../ui/Card';
import { RARITY_CONFIG } from '../../../mascot/achievements/achievementData';
import { RarityDjayWrapper } from './RarityDjayWrapper';
import { useAchievementShimmer } from './useAchievementShimmer';
import { AchievementProgressBar } from './AchievementProgressBar';
import type { AchievementCardProps } from './types';
export function AchievementCard({
  achievement,
  isUnlocked,
  index
}: AchievementCardProps) {
  const isNewlySeen = useAchievementShimmer(achievement.id, isUnlocked);
  const config = RARITY_CONFIG[achievement.rarity];
  const isHighlightRarity = achievement.rarity === 'rare' || achievement.rarity === 'legendary';
  const djayEmotion = isUnlocked ? achievement.djayEmotionUnlocked : achievement.djayEmotionLocked;
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.02
  }} className="relative h-full" data-id="element-1742" data-mp="1i1pee61cp6rc">
      <Card className={`
          relative overflow-hidden p-3 transition-all duration-300 border-2 h-full flex flex-col
          ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-70'}
          ${isUnlocked ? 'hover:shadow-md hover:-translate-y-0.5' : ''}
          ${isNewlySeen ? 'animate-shine' : ''}
        `} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1743" data-mp="2hbh87fdr2txx">
        {!isUnlocked && <div className="absolute top-2 right-2 z-10" data-id="element-1744" data-mp="1on37usz6vlj7">
            <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shadow-inner" data-id="element-1745" data-mp="1ood5xi61ktpu">
              <Lock className="w-3 h-3 text-slate-400" data-id="element-1746" data-mp="3nzedymtqmzl9" />
            </div>
          </div>}

        {isUnlocked && isHighlightRarity && <div className="absolute top-2 right-2 z-10" data-id="element-1747" data-mp="1hc6w71eofg4s">
            <div className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold shadow-sm ${config.badgeBg} ${config.badgeText}`} data-id="element-1748" data-mp="1ham55lrkmfbo">
              {config.label}
            </div>
          </div>}

        <div className="flex items-start gap-3 relative z-10 flex-1" data-id="element-1749" data-mp="1ccu2pxtavat8">
          <div className="flex-shrink-0" data-id="element-1750" data-mp="1cd3splechhyx">
            <RarityDjayWrapper rarity={achievement.rarity} emotion={djayEmotion} variant={achievement.djayVariantUnlocked} accessory={achievement.accessory} isUnlocked={isUnlocked} animate={isUnlocked} data-id="element-1751" data-mp="201wtbv5wwdzv" />
          </div>

          <div className="flex-1 min-w-0 pt-0.5 flex flex-col h-full" data-id="element-1752" data-mp="1v2k453rkiuu4">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-1753" data-mp="fmtfw4chr4ek">
              <span className="text-base" data-id="element-1754" data-mp="fla0sh2kmzed">{achievement.emoji}</span>
              <h4 className={`font-black text-xs truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1755" data-mp="jp7xym8c604s">
                {achievement.name}
              </h4>
            </div>
            <p className={`text-[10px] leading-snug mb-2 flex-1 ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1756" data-mp="74rjx2szhida">
              {achievement.description}
            </p>

            {!isUnlocked && achievement.progress && <div className="mt-auto pt-1" data-id="element-1757" data-mp="3pjaf71jwzy6d">
                <AchievementProgressBar current={achievement.progress.current} max={achievement.progress.max} layout="labelled" size="thin" data-id="element-1758" data-mp="3pk1zvhbz4bhv" />
              </div>}
          </div>
        </div>
      </Card>
    </motion.div>;
}